import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSEOData,
  generateTitle,
  optimizeMetaDescription,
  generateKeywords,
  getCanonicalUrl,
  type SEOData
} from '@/utils/seo';

interface SEOHeadProps {
  pageKey?: string;
  customSEO?: Partial<SEOData>;
}

const SEOHead: React.FC<SEOHeadProps> = ({ pageKey, customSEO }) => {
  const location = useLocation();

  // Determine page key from current route if not provided
  const currentPageKey = pageKey || (() => {
    const path = location.pathname;
    switch (path) {
      case '/': return 'homepage';
      case '/about': return 'about';
      case '/faq': return 'faq';
      case '/contact': return 'contact';
      case '/privacy-policy': return 'privacy';
      case '/terms-of-service': return 'terms';
      default: return 'homepage';
    }
  })();

  // Get SEO data and merge with custom overrides
  const seoData = getSEOData(currentPageKey);
  const finalSEO = { ...seoData, ...customSEO };

  useEffect(() => {
    // Update document title
    document.title = generateTitle(finalSEO.title);

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
      }

      tag.setAttribute('content', content);
    };

    // Update canonical link
    const updateCanonicalLink = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]');

      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }

      link.setAttribute('href', href);
    };

    // Update JSON-LD structured data
    const updateJsonLd = (data: object) => {
      // Remove existing JSON-LD
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new JSON-LD
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    // Basic meta tags
    updateMetaTag('description', optimizeMetaDescription(finalSEO.description));

    if (finalSEO.keywords) {
      updateMetaTag('keywords', generateKeywords(finalSEO.keywords));
    }

    // Robots meta tag
    const robotsContent = [];
    if (finalSEO.noindex) robotsContent.push('noindex');
    if (finalSEO.nofollow) robotsContent.push('nofollow');
    if (robotsContent.length === 0) robotsContent.push('index', 'follow');
    updateMetaTag('robots', robotsContent.join(', '));

    // Canonical URL
    const canonicalPath = finalSEO.canonical || location.pathname;
    updateCanonicalLink(getCanonicalUrl(canonicalPath));

    // Open Graph tags
    updateMetaTag('og:title', finalSEO.ogTitle || finalSEO.title, true);
    updateMetaTag('og:description', optimizeMetaDescription(finalSEO.ogDescription || finalSEO.description), true);
    updateMetaTag('og:type', finalSEO.ogType || 'website', true);
    updateMetaTag('og:url', getCanonicalUrl(canonicalPath), true);

    if (finalSEO.ogImage) {
      const imageUrl = finalSEO.ogImage.startsWith('http')
        ? finalSEO.ogImage
        : `${getCanonicalUrl('')}${finalSEO.ogImage.replace(/^\//, '')}`;
      updateMetaTag('og:image', imageUrl, true);
      updateMetaTag('og:image:alt', `${finalSEO.title} - Social media preview`, true);
      updateMetaTag('og:image:width', '1200', true);
      updateMetaTag('og:image:height', '630', true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', finalSEO.twitterCard || 'summary_large_image');
    updateMetaTag('twitter:title', finalSEO.twitterTitle || finalSEO.title);
    updateMetaTag('twitter:description', optimizeMetaDescription(finalSEO.twitterDescription || finalSEO.description));

    if (finalSEO.twitterImage) {
      const twitterImageUrl = finalSEO.twitterImage.startsWith('http')
        ? finalSEO.twitterImage
        : `${getCanonicalUrl('')}${finalSEO.twitterImage.replace(/^\//, '')}`;
      updateMetaTag('twitter:image', twitterImageUrl);
      updateMetaTag('twitter:image:alt', `${finalSEO.title} - Twitter preview`);
    }

    // Site-specific Twitter handle
    updateMetaTag('twitter:site', '@wabisabiapp');
    updateMetaTag('twitter:creator', '@wabisabiapp');

    // Additional meta tags for better SEO
    updateMetaTag('author', 'Wabisabi Team');
    updateMetaTag('language', 'en');
    updateMetaTag('theme-color', '#6C4F77');
    updateMetaTag('msapplication-TileColor', '#6C4F77');
    updateMetaTag('application-name', 'Wabisabi');
    updateMetaTag('apple-mobile-web-app-title', 'Wabisabi');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');

    // JSON-LD structured data
    if (finalSEO.jsonLd) {
      updateJsonLd(finalSEO.jsonLd);
    }

    // Additional JSON-LD for organization (on all pages)
    const organizationJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Wabisabi',
      url: getCanonicalUrl(''),
      logo: `${getCanonicalUrl('')}/wabisabi-logo.png`,
      description: 'AI-powered relationship memory app that helps you remember what matters most',
      email: 'hello@appwabisabi.com',
      sameAs: [
        'https://www.linkedin.com/company/appwabisabi/about/',
        'https://www.instagram.com/appwabisabi/',
        'https://twitter.com/wabisabiapp'
      ],
      founder: {
        '@type': 'Person',
        name: 'Atinder'
      }
    };

    // Add organization JSON-LD if no custom JSON-LD is provided
    if (!finalSEO.jsonLd) {
      updateJsonLd(organizationJsonLd);
    }

  }, [currentPageKey, finalSEO, location.pathname]);

  // This component doesn't render anything - it only manages head tags
  return null;
};

export default SEOHead;
